import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expensesKeys } from '../expenses/queries';
import { unwrap } from '../../data/api';
import { invalidateFinancialData } from '../financialCache';
import { invalidateGuidedFlow } from '../guidedFlow/queries';
import { potsAPI } from '../../data/pots';

export const potsKeys = {
  all: ['pots'],
  list: (params) => [...potsKeys.all, 'list', params || {}],
  detail: (id) => [...potsKeys.all, 'detail', id],
  history: (id) => [...potsKeys.all, 'history', id],
  monthlyCommitment: () => [...potsKeys.all, 'monthly-commitment'],
};


export function usePotsQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: potsKeys.list(params),
    queryFn: () => potsAPI.list(params).then((r) => unwrap(r, [])),
    ...queryOptions,
  });
}

export function usePotQuery(id, queryOptions = {}) {
  return useQuery({
    queryKey: potsKeys.detail(id),
    queryFn: () => potsAPI.retrieve(id).then((r) => unwrap(r)),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

// CHANGE v2 §24: histórico de depósitos é uma lista de Expense.
export function usePotHistoryQuery(id, queryOptions = {}) {
  return useQuery({
    queryKey: potsKeys.history(id),
    queryFn: () => potsAPI.history(id).then((r) => unwrap(r, [])),
    enabled: Boolean(id),
    ...queryOptions,
  });
}

export function useMonthlyCommitmentQuery(queryOptions = {}) {
  return useQuery({
    queryKey: potsKeys.monthlyCommitment(),
    queryFn: () => potsAPI.monthlyCommitment().then((r) => unwrap(r)),
    ...queryOptions,
  });
}

export function useCreatePotFromOccasionalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (occasionalId) =>
      potsAPI.createFromOccasional(occasionalId).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateFinancialData(qc);
      qc.invalidateQueries({ queryKey: ['calendar'], exact: false });
      qc.invalidateQueries({ queryKey: expensesKeys.annual() });
      invalidateGuidedFlow(qc);
    },
  });
}

// CHANGE v2 §24: fechar cofre — REVERT_ALL apaga despesas, KEEP_HISTORY preserva.
export function useClosePotMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }) =>
      potsAPI.close(id, action).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateFinancialData(qc);
    },
  });
}

export function useTransferPotMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ fromPotId, toPotId, amount }) =>
      potsAPI
        .transfer({ fromPotId, toPotId, amount })
        .then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateFinancialData(qc);
      qc.invalidateQueries({ queryKey: expensesKeys.all, exact: false });
    },
  });
}
